import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
export const BarChart: React.FC<any> = ({ data }) => {
    const chartRef = useRef<SVGSVGElement | null>(null);
    const colors = [
        "#0340a6",
        "#7c8eff",
        "#60e0e2",
        "#4cadee",
        "#2782b3",
        "#05105a",
    ];
    useEffect(() => {
        const svg = d3.select(chartRef.current);
        svg.selectAll("*").remove();
        const margin = { top: 40, right: 30, bottom: 80, left: 40 };
        const width = 340 - margin.left - margin.right;
        const height = 300 - margin.top - margin.bottom;
        const chartData = Object.keys(data.loan_composition.focus_quarter)
            .map((key) => ({
                label: data.loan_composition.focus_quarter[key].label,
                value: data.loan_composition.focus_quarter[key].value,
            }))
            .filter((d) => d.value > 0);
        const x = d3
            .scaleBand()
            .domain(chartData.map((d) => d.label))
            .range([0, width])
            .padding(0.2);
        const y = d3
            .scaleLinear()
            .domain([0, d3.max(chartData, (d) => d.value) || 0])
            .nice()
            .range([height, 0]);
        const svgElement = svg
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);
        const sanitizeClass = (str: string) => str.replace(/[^a-zA-Z0-9]/g, "-");
        const MIN_BAR_HEIGHT = 5;
        const bars = svgElement
            .append("g")
            .selectAll("rect")
            .data(chartData)
            .enter()
            .append("rect")
            .attr("x", (d) => x(d.label) || 0)
            .attr("y", (d) => Math.min(y(d.value), height - MIN_BAR_HEIGHT))
            .attr("width", x.bandwidth())
            .attr("height", (d) => Math.max(MIN_BAR_HEIGHT, height - y(d.value)))
            .attr("fill", (_d, i) => colors[i % colors.length])
            .attr("cursor", "pointer")
            .attr("opacity", 1);
        bars.on("mouseenter", function (_, d) {
            d3.select(this).transition().duration(200).attr("opacity", 0.7);
            const sanitizedLabel = sanitizeClass(d.label);
            svgElement
                .append("text")
                .attr("x", (x(d.label) || 0) + x.bandwidth() / 2)
                .attr("y", y(d.value) - 10)
                .attr("text-anchor", "middle")
                .style("fill", "black")
                .style("font-size", "9px")
                .attr("opacity", 0)
                .text(d.value)
                .transition()
                .duration(200)
                .attr("opacity", 1)
                .attr("class", `value-label-${sanitizedLabel}`);
            svgElement
                .append("text")
                .attr("x", (x(d.label) || 0) + x.bandwidth() / 2)
                .attr("y", height + 20)
                .attr("dy", "0.71em")
                .attr("text-anchor", "middle")
                .style("fill", "black")
                .style("font-size", "9px")
                .attr("opacity", 0)
                .text(d.label)
                .transition()
                .duration(200)
                .attr("opacity", 1)
                .attr("class", `label-${sanitizedLabel}`);
        });

        bars.on("mouseleave", function (_, d) {
            d3.select(this).transition().duration(200).attr("opacity", 1);

            const sanitizedLabel = sanitizeClass(d.label);

            svgElement
                .select(`.value-label-${sanitizedLabel}`)
                .transition()
                .duration(200)
                .attr("opacity", 0)
                .remove();

            svgElement
                .select(`.label-${sanitizedLabel}`)
                .transition()
                .duration(200)
                .attr("opacity", 0)
                .remove();
        });
        svgElement
            .append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x).tickFormat(() => ""));
        svgElement
            .append("g")
            .call(d3.axisLeft(y))
            .selectAll("text")
            .style("font-size", "8px")
            .style("fill", "black");
    }, [data]);
    const year = data.loan_composition.focus_quarter.year;
    const quarter = data.loan_composition.focus_quarter.quarter;
    return (
        <div className="BarChart-section">
            <div className="bar-graph-section">
                <svg ref={chartRef} style={{ overflow: "visible" }}></svg>
            </div>
            <div className="barChart-text-section">
                <div className="loanComp-year-heading">
                    {year} <span>Q{quarter}</span>
                </div>
            </div>
        </div>
    );
};
